// A walkthrough (design/2026-10-20-walkthroughs.md): a small card that drives
// the real page step by step, written as a file beside the feature it shows
// and run by the Help pane's tour engine (help/useTourRunner) with authored
// text in place of the help model. Docs: docs/walkthroughs.md.
//
// Files in this folder import types only, never an app module at run time:
// the Playwright spec (e2e/tests/44-walkthroughs.spec.ts) imports the index.

import type { HelpReader } from '../features'
import type { TourRun, TourStep } from '../tourRuntime'

/**
 * One step: what the card says, and what the runner does on entering it.
 * The step words are the FleetView voice walkthrough point's
 * ({text, url?, selector?, actions?}) under Platinum's names: say, page or
 * route, control, and one of action (a page tool), click or fill.
 * `type` (send to the agent) is left out on purpose: a walkthrough sends nothing.
 */
export type WalkStep = Omit<TourStep, 'type'> & { say: string }

export interface Walkthrough {
  /** The ?walkthrough= value: lower-case letters, digits and hyphens. Never rename one: links and done ticks use it. */
  slug: string
  title: string
  /** One or two sentences on the first card: what the person will see. */
  summary: string
  /** The issue the feature shipped for, e.g. "#1912". */
  issue?: string
  /** When the file was added, YYYY-MM-DD. */
  added: string
  /** Who may start it; Managers only when unset. */
  audience?: HelpReader
  /** Why the e2e spec does not run it (it needs data the fixture job lacks); unset, it runs. */
  skipE2E?: string
  steps: WalkStep[]
}

const READER_RANK: Record<HelpReader, number> = { public: 0, realm: 1, manager: 2 }

/** Whether this reader may start the walkthrough. */
export function canStartWalkthrough(reader: HelpReader, w: Walkthrough): boolean {
  return READER_RANK[reader] >= READER_RANK[w.audience ?? 'manager']
}

/** The walkthrough as the tour engine runs it. */
export function walkthroughToRun(w: Walkthrough): TourRun {
  return { id: `walkthrough:${w.slug}`, name: w.title, steps: w.steps }
}
